import { Component, Watch, Prop } from 'vue-property-decorator';
import BaseBlock from '@/blocks/base-block';
import { LocationDTO } from '../../../model/LocationDTO';
import { Orientation } from '../../../enums/Orientation';
import { AmenityType } from '../../../enums/AmenityType';
import { ApartmentType } from '../../../enums/ApartmentType';
import { ApartmentSearchFilterDTO } from '../../../model/ApartmentSearchFilterDTO';
import { formatDateLocalISO } from '../../../helpers/dates';
import { ApartmentSearchStatsDTO } from '../../../model/ApartmentSearchStatsDTO';
import { ApartmentSearchSortType } from '@/enums/ApartmentSearchSortType';
import { ApartmentSearchSortDirection } from '../../../enums/ApartmentSearchSortDirection';
import { Block } from '../../../helpers/components';
import { apartmentSearchFormData } from './apartment-search-form.data';
import { LocationStatsDTO } from '../../../model/LocationStatsDTO';

import { union, difference, range, flatten, uniq, unionBy, keyBy } from 'lodash-es';

interface FormValues {
	locationIds: number[];
	priceRange: [number, number];
	layouts: number[];
	completionDate?: Date;
	completedOnly: boolean;
	floorAreaRange: [number, number];
	orientations: Orientation[];
	floorLevelRange: [any, any];
	amenities: AmenityType[];
	apartmentTypes: ApartmentType[];
	sortType: ApartmentSearchSortType;
}

@Block(apartmentSearchFormData)
@Component
export class ApartmentSearchFormBlock extends BaseBlock {
	@Prop() locations: LocationDTO[];
	@Prop() locationStats: LocationStatsDTO[];

	formValues: FormValues = {
		locationIds: [],
		priceRange: [0, 0],
		layouts: [],
		completionDate: null,
		completedOnly: false,
		floorAreaRange: [0, 0],
		orientations: [],
		floorLevelRange: [0, 0],
		amenities: [],
		apartmentTypes: [],
		sortType: ApartmentSearchSortType.TotalPrice
	};

	showExtendedFilter: boolean = false;

	searchStatsPromise: Promise<ApartmentSearchStatsDTO> = null;
	searchStats: ApartmentSearchStatsDTO = null;

	get formIsSubmitted() {
		return this.$route.query.search === 'true';
	}

	created() {
		this.formValues.priceRange = [this.minPriceOption, this.maxPriceOption];
		this.formValues.completionDate = this.latestCompletionDateOption;
		this.formValues.floorAreaRange = [this.minFloorAreaOption, this.maxFloorAreaOption];
		this.formValues.floorLevelRange = [this.minFloorLevelOption, this.maxFloorLevelOption];

		// set values from URL query string
		const queryString = {...this.$route.query};

		console.log(queryString);

		if (queryString.locationIds) {
			this.formValues.locationIds = (queryString.locationIds as any as string[]).map(i => parseInt(i));
		}

		if (queryString.minPrice) {
			this.formValues.priceRange[0] = parseInt(queryString.minPrice);
		}

		if (queryString.maxPrice) {
			this.formValues.priceRange[1] = parseInt(queryString.maxPrice);
		}

		if (queryString.layoutRoomCounts) {
			this.formValues.layouts = (queryString.layoutRoomCounts as any as string[]).map(c => parseInt(c));
		}

		if (queryString.completionDate) {
			const completionDate = new Date(queryString.completionDate);

			if (completionDate.getMonth() <= 5) {
				this.formValues.completionDate = new Date(completionDate.getFullYear(), 5, 30);
			} else {
				this.formValues.completionDate = new Date(completionDate.getFullYear(), 11, 31);
			}

			this.formValues.completedOnly = false;
		}

		if (queryString.completedOnly) {
			this.formValues.completedOnly = true;
		}

		if (queryString.minArea) {
			this.formValues.floorAreaRange[0] = parseInt(queryString.minArea);
			this.showExtendedFilter = true;
		}

		if (queryString.maxArea) {
			this.formValues.floorAreaRange[1] = parseInt(queryString.maxArea);
			this.showExtendedFilter = true;
		}

		if (queryString.orientations) {
			this.formValues.orientations = (queryString.orientations as any as string[]).map(o => parseInt(o));
			this.showExtendedFilter = true;
		}

		if (queryString.floorLevels) {
			const floorLevels = (queryString.floorLevels as any as string[]).map(l => parseInt(l));
			this.formValues.floorLevelRange = [
				Math.min(...floorLevels),
				Math.max(...floorLevels)
			];
			this.showExtendedFilter = true;
		}

		if (queryString.amenities) {
			this.formValues.amenities = (queryString.amenities as any as string[]).map(a => parseInt(a));
			this.showExtendedFilter = true;
		}

		if (queryString.apartmentTypes) {
			this.formValues.apartmentTypes = (queryString.apartmentTypes as any as string[]).map(t => parseInt(t));
			this.showExtendedFilter = true;
		}

		if (queryString.sort) {
			this.formValues.sortType = parseInt(queryString.sort);
		}

		console.log(this.formValues);
	}

	/* Locations */

	get locationById() {
		return keyBy(this.locations, 'id');
	}

	get locationsSelected() {
		return this.formValues.locationIds.length > 0;
	}

	get selectedLocationNames() {
		return uniq(this.formValues.locationIds.map(id => {
			const location = this.locationById[id];

			var name = location.city;
			if (location.cityPart) {
				name += ` - ${location.cityPart}`;
			}

			return name;
		}));
	}

	handleCityIdChange(event: Event) {
		const {value, checked} = event.target as HTMLInputElement;

		this.toggleLocations(
			this.locations.filter(l => l.cityId === parseInt(value)).map(l => l.id),
			checked
		);
	}

	handleCityPartIdChange(event: Event) {
		const {value, checked} = event.target as HTMLInputElement;

		this.toggleLocations(
			this.locations.filter(l => l.cityPartId === parseInt(value)).map(l => l.id),
			checked
		);
	}

	private toggleLocations(locationIds: number[], select: boolean) {
		this.formValues.locationIds = select
			? union(this.formValues.locationIds, locationIds)
			: difference(this.formValues.locationIds, locationIds);
	}

	/* Price */

	get minPriceOption() {
		return Math.floor(Math.min(...this.locationStats.map(s => s.minAvailablePrice)) / 1e6) * 1e6;
	}

	get maxPriceOption() {
		return Math.ceil(Math.max(...this.locationStats.map(s => s.maxAvailablePrice)) / 1e6) * 1e6;
	}

	get minPriceSelected() {
		return this.formValues.priceRange[0] > this.minPriceOption;
	}

	get maxPriceSelected() {
		return this.formValues.priceRange[1] < this.maxPriceOption;
	}

	get priceSelected() {
		return this.minPriceSelected
			|| this.maxPriceSelected;
	}

	/* Layout */

	get layoutOptions() {
		const layoutStats = this.locationStats.reduce((layoutStats, s) => unionBy(layoutStats, s.layoutStats, 'layoutId'), []);

		return layoutStats.map(l => ({
			label: l.layoutLabel,
			value: l.layoutRoomCount
		}));
	}

	get layoutsSelected() {
		return this.formValues.layouts.length > 0;
	}

	/* Completion date */

	get completionDateOptions() {
		if (! this.locationStats) {
			return [];
		}

		const locationLastCompletionDates = this.locationStats.map(s => s.lastApartmentCompletionDate).sort().reverse();
		const lastCompletionDate = new Date(locationLastCompletionDates[0]);

		const currentDate = new Date();
		const yearRange = range(currentDate.getFullYear(), lastCompletionDate.getFullYear() + 1);
		const options = flatten(yearRange.map(year => [
			new Date(year, 5, 30), // half of a year (30th June)
			new Date(year, 11, 31), // end of a year (31th December)
		]));

		if (currentDate.getMonth() > 5) {
			// remove half of current year option if today is at least July
			options.shift();
		}

		if (lastCompletionDate.getMonth() <= 5) {
			// remove end of max complention year option if max completion date is at most June
			options.pop();
		}

		return options;
	}

	get latestCompletionDateOption() {
		return this.completionDateOptions[this.completionDateOptions.length - 1];
	}

	get completionDateSelected() {
		return (this.formValues.completionDate && (this.formValues.completionDate.getTime() !== this.latestCompletionDateOption.getTime()))
			|| this.formValues.completedOnly;
	}

	/* Floor area */

	get minFloorAreaOption() {
		return Math.round(Math.min(...this.locationStats.map(s => s.minAvailableArea)));
	}

	get maxFloorAreaOption() {
		return Math.round(Math.max(...this.locationStats.map(s => s.maxAvailableArea)));
	}

	get minFloorAreaSelected() {
		return this.formValues.floorAreaRange[0] > this.minFloorAreaOption;
	}

	get maxFloorAreaSelected() {
		return this.formValues.floorAreaRange[1] < this.maxFloorAreaOption;
	}

	get floorAreaSelected() {
		return this.minFloorAreaSelected
			|| this.maxFloorAreaSelected;
	}

	/* Floor level */

	get minFloorLevelOption() {
		return Math.round(Math.min(...this.locationStats.map(s => s.minAvailableFloorLevel)));
	}

	get maxFloorLevelOption() {
		return Math.round(Math.max(...this.locationStats.map(s => s.maxAvailableFloorLevel)));
	}

	get minFloorLevelSelected() {
		return this.formValues.floorLevelRange[0] > this.minFloorLevelOption;
	}

	get maxFloorLevelSelected() {
		return this.formValues.floorLevelRange[1] < this.maxFloorLevelOption;
	}

	get floorLevelSelected() {
		return this.minFloorLevelSelected
			|| this.maxFloorLevelSelected;
	}

	/* Orientation */

	get orientationsSelected() {
		return this.formValues.orientations.length > 0;
	}

	/* Amenities */

	get amenitiesSelected() {
		return this.formValues.amenities.length > 0;
	}

	/* Apartment type */

	get apartmentTypesSelected() {
		return this.formValues.apartmentTypes.length > 0;
	}

	/* Params */

	get paramsSelected() {
		return this.locationsSelected
			|| this.priceSelected
			|| this.layoutsSelected
			|| this.completionDateSelected
			|| this.otherParamsSelected;
	}

	get otherParamsSelected() {
		return this.floorAreaSelected
			|| this.floorLevelSelected
			|| this.orientationsSelected
			|| this.amenitiesSelected
			|| this.apartmentTypesSelected;
	}

	get searchFilter() {
		console.log(this.formValues);
		const searchFilter: ApartmentSearchFilterDTO = {};

		/* Locations */

		if (this.locationsSelected) {
			searchFilter.locationIds = this.formValues.locationIds;
		}

		/* Layouts */

		if (this.layoutsSelected) {
			searchFilter.layoutRoomCounts = [...this.formValues.layouts];
		}

		/* Floor area */

		if (this.minFloorAreaSelected) {
			searchFilter.minArea = this.formValues.floorAreaRange[0];
		}

		if (this.maxFloorAreaSelected) {
			searchFilter.maxArea = this.formValues.floorAreaRange[1];
		}

		/* Floor level */

		if (this.minFloorLevelSelected || this.maxFloorLevelSelected) {
			searchFilter.floorLevels = range(this.formValues.floorLevelRange[0], this.formValues.floorLevelRange[1] + 1);
		}

		/* Price */

		if (this.minPriceSelected) {
			searchFilter.minPrice = this.formValues.priceRange[0];
		}

		if (this.maxPriceSelected) {
			searchFilter.maxPrice = this.formValues.priceRange[1];
		}

		if (this.minPriceSelected && this.maxPriceSelected && searchFilter.minPrice === searchFilter.maxPrice) {
			searchFilter.minPrice -= 0.5e6;
			searchFilter.maxPrice += 0.5e6;
		}

		/* Completion date */

		if (this.completionDateSelected) {
			if (this.formValues.completedOnly) {
				searchFilter.completedOnly = true;
			} else {
				searchFilter.completionDate = formatDateLocalISO(this.formValues.completionDate);
			}
		}

		/* Orientation */

		if (this.orientationsSelected) {
			searchFilter.orientations = [...this.formValues.orientations];
		}

		/* Amenities */

		if (this.amenitiesSelected) {
			searchFilter.amenities = [...this.formValues.amenities];
		}

		/* Apartment type */

		if (this.apartmentTypesSelected) {
			searchFilter.apartmentTypes = [...this.formValues.apartmentTypes];
		}

		/* Returned to offer */

		if ('returnedToOffer' in this.$route.query) {
			searchFilter.returnedToOffer = this.$route.query.returnedToOffer === 'true';
		}

		/* Time version */

		if (this.$route.query.csh) {
			searchFilter.csh = parseInt(this.$route.query.csh);
		}

		/* Preview (for showing preparation time version) */

		if (this.$route.query.predbezne) {
			searchFilter.predbezne = parseInt(this.$route.query.predbezne);
		}

		return searchFilter;
	}

	sortTypeOnChange() {
		this.search();
	}

	@Watch('searchFilter')
	formValuesChanged() {
		// TODO debounce or throttle
		this.searchStats = null;

		const promise = this.$apartmentService.fetchSearchStats(this.searchFilter);
		promise.then((stats) => {
			// check if this is a response for the latest request
			if (promise === this.searchStatsPromise) {
				this.searchStats = stats;
			}
		})

		this.searchStatsPromise = promise;
	}

	search() {
		this.$router.push({path: this.$route.path, query: {
			...this.searchFilter as any,
			sort: this.formValues.sortType,
			sortDirection: ApartmentSearchSortDirection.Up,
			search: true
		}});
	}

	goBackToForm() {
		this.$router.push({path: this.$route.path, query: {
			...this.searchFilter as any
		}});
	}
}

export default ApartmentSearchFormBlock;
